import {
  useCallback,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent,
  type PointerEvent,
  type ReactNode
} from "react";
import { createPortal } from "react-dom";
import {
  DEFAULT_OVERFLOW_PREVIEW_CHARACTERS,
  overflowPopoverPosition,
  overflowPreview,
  type OverflowRevealKind
} from "./overflowReveal.js";
import "./overflow-reveal.css";

type RevealMode = "closed" | "preview" | "expanded";
type OverflowRevealElement = "span" | "strong" | "small" | "code" | "h1" | "p" | "dd";
type PopoverPosition = ReturnType<typeof overflowPopoverPosition>;

let activeReveal: (() => void) | null = null;

const claimReveal = (close: () => void): void => {
  if (activeReveal && activeReveal !== close) activeReveal();
  activeReveal = close;
};

const releaseReveal = (close: () => void): void => {
  if (activeReveal === close) activeReveal = null;
};

const copyText = async (value: string): Promise<boolean> => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(value);
      return true;
    }
  } catch {
    // Fall back to the selection-based copy path below.
  }
  const textarea = document.createElement("textarea");
  try {
    textarea.value = value;
    textarea.setAttribute("readonly", "");
    textarea.style.position = "fixed";
    textarea.style.opacity = "0";
    textarea.style.pointerEvents = "none";
    document.body.appendChild(textarea);
    textarea.select();
    return document.execCommand("copy");
  } catch {
    return false;
  } finally {
    textarea.remove();
  }
};

const withinViewport = (element: HTMLElement): boolean => {
  const bounds = element.getBoundingClientRect();
  return bounds.bottom > 0
    && bounds.right > 0
    && bounds.top < window.innerHeight
    && bounds.left < window.innerWidth;
};

export type OverflowRevealProps = {
  value: string;
  displayValue?: string;
  as?: OverflowRevealElement;
  kind?: OverflowRevealKind;
  label?: string;
  className?: string;
  copyable?: boolean;
  focusable?: boolean;
  forceReveal?: boolean;
  previewCharacters?: number;
  children?: ReactNode;
  testId?: string;
};

export function OverflowReveal({
  value,
  displayValue,
  as: Element = "span",
  kind = "text",
  label,
  className,
  copyable = false,
  focusable = false,
  forceReveal = false,
  previewCharacters = DEFAULT_OVERFLOW_PREVIEW_CHARACTERS,
  children,
  testId
}: OverflowRevealProps) {
  const triggerRef = useRef<HTMLElement | null>(null);
  const panelRef = useRef<HTMLElement | null>(null);
  const previewBodyRef = useRef<HTMLDivElement | null>(null);
  const closeTimer = useRef<number | null>(null);
  const copyTimer = useRef<number | null>(null);
  const updateFrame = useRef<number | null>(null);
  const selectingText = useRef(false);
  const [mode, setMode] = useState<RevealMode>("closed");
  const [position, setPosition] = useState<PopoverPosition | null>(null);
  const [previewVisuallyClipped, setPreviewVisuallyClipped] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const id = `overflow-reveal-${useId().replaceAll(":", "")}`;
  const preview = useMemo(
    () => overflowPreview(value, previewCharacters, kind),
    [kind, previewCharacters, value]
  );
  const expandable = preview.truncated || previewVisuallyClipped;
  const interactive = copyable || expandable;
  const shownValue = displayValue ?? value;

  const clearCloseTimer = useCallback(() => {
    if (closeTimer.current != null) {
      window.clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  }, []);

  const close = useCallback(() => {
    clearCloseTimer();
    setMode("closed");
    setPosition(null);
    setPreviewVisuallyClipped(false);
    setCopied(false);
    setCopyFailed(false);
    selectingText.current = false;
    releaseReveal(close);
  }, [clearCloseTimer]);

  const shouldReveal = useCallback((): boolean => {
    const trigger = triggerRef.current;
    if (!trigger || !value) return false;
    return forceReveal
      || (copyable && focusable)
      || shownValue !== value
      || trigger.scrollWidth > trigger.clientWidth + 1
      || trigger.scrollHeight > trigger.clientHeight + 1;
  }, [copyable, focusable, forceReveal, shownValue, value]);

  const openPreview = useCallback(() => {
    clearCloseTimer();
    if (!shouldReveal()) return;
    claimReveal(close);
    setMode((current) => current === "expanded" ? current : "preview");
  }, [clearCloseTimer, close, shouldReveal]);

  const scheduleClose = useCallback(() => {
    clearCloseTimer();
    if (mode === "expanded" || selectingText.current) return;
    closeTimer.current = window.setTimeout(() => {
      const panelHasFocus = panelRef.current?.contains(document.activeElement) ?? false;
      const triggerHasFocus = triggerRef.current?.contains(document.activeElement) ?? false;
      if (!panelHasFocus && !triggerHasFocus) close();
    }, 220);
  }, [clearCloseTimer, close, mode]);

  const updatePosition = useCallback(() => {
    if (updateFrame.current != null) window.cancelAnimationFrame(updateFrame.current);
    updateFrame.current = window.requestAnimationFrame(() => {
      updateFrame.current = null;
      const trigger = triggerRef.current;
      const panel = panelRef.current;
      if (!trigger || !panel) return;
      if (!withinViewport(trigger)) {
        close();
        return;
      }
      const anchor = trigger.getBoundingClientRect();
      const panelBounds = panel.getBoundingClientRect();
      const next = overflowPopoverPosition(
        anchor,
        { width: panelBounds.width, height: panelBounds.height },
        { width: window.innerWidth, height: window.innerHeight }
      );
      setPosition((current) => current
        && Math.abs(current.top - next.top) < .5
        && Math.abs(current.left - next.left) < .5
        && current.side === next.side
        ? current
        : next);
    });
  }, [close]);

  useLayoutEffect(() => {
    if (mode === "closed") return;
    updatePosition();
  }, [mode, preview.text, updatePosition]);

  useLayoutEffect(() => {
    if (mode !== "preview") return;
    const body = previewBodyRef.current;
    if (!body) return;
    const measure = () => setPreviewVisuallyClipped(
      body.scrollHeight > body.clientHeight + 1 || body.scrollWidth > body.clientWidth + 1
    );
    measure();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(body);
    return () => observer?.disconnect();
  }, [mode, preview.text]);

  useEffect(() => {
    if (mode === "closed") return;
    const onPointerDown = (event: globalThis.PointerEvent) => {
      const target = event.target as Node | null;
      if (target && (triggerRef.current?.contains(target) || panelRef.current?.contains(target))) return;
      close();
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      const focusTarget = triggerRef.current?.closest<HTMLElement>("button, a, [tabindex]") ?? triggerRef.current;
      close();
      focusTarget?.focus({ preventScroll: true });
    };
    const onPointerUp = (event: globalThis.PointerEvent) => {
      if (!selectingText.current) return;
      selectingText.current = false;
      const target = event.target as Node | null;
      if (target && (triggerRef.current?.contains(target) || panelRef.current?.contains(target))) return;
      scheduleClose();
    };
    const onViewportChange = () => updatePosition();
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("pointerup", onPointerUp, true);
    document.addEventListener("keydown", onKeyDown);
    window.addEventListener("resize", onViewportChange);
    window.addEventListener("scroll", onViewportChange, true);
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(onViewportChange);
    if (triggerRef.current) observer?.observe(triggerRef.current);
    if (panelRef.current) observer?.observe(panelRef.current);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("pointerup", onPointerUp, true);
      document.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("resize", onViewportChange);
      window.removeEventListener("scroll", onViewportChange, true);
      observer?.disconnect();
    };
  }, [close, mode, scheduleClose, updatePosition]);

  useEffect(() => {
    close();
  }, [close, value]);

  useEffect(() => () => {
    clearCloseTimer();
    if (copyTimer.current != null) window.clearTimeout(copyTimer.current);
    if (updateFrame.current != null) window.cancelAnimationFrame(updateFrame.current);
    releaseReveal(close);
  }, [clearCloseTimer, close]);

  const stopPanelEvent = (event: PointerEvent<HTMLElement> | MouseEvent<HTMLElement>) => {
    event.stopPropagation();
  };

  const onPanelPointerDown = (event: PointerEvent<HTMLElement>) => {
    clearCloseTimer();
    selectingText.current = true;
    stopPanelEvent(event);
  };

  const onPanelKeyDown = (event: ReactKeyboardEvent<HTMLElement>) => {
    if (event.key !== "Escape") event.stopPropagation();
  };

  const onCopy = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    clearCloseTimer();
    void copyText(value).then((success) => {
      setCopied(success);
      setCopyFailed(!success);
      if (copyTimer.current != null) window.clearTimeout(copyTimer.current);
      copyTimer.current = window.setTimeout(() => {
        setCopied(false);
        setCopyFailed(false);
      }, 1_500);
    });
  };

  const onToggleExpanded = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    clearCloseTimer();
    setMode((current) => current === "expanded" ? "preview" : "expanded");
  };

  const panel = mode === "closed" || typeof document === "undefined" ? null : createPortal(
    <aside
      ref={panelRef}
      id={id}
      className={`overflow-reveal-popover overflow-reveal-popover--${mode} overflow-reveal-popover--${kind}`}
      role={interactive ? "dialog" : "tooltip"}
      aria-label={interactive ? label ?? (kind === "path" ? "Full path" : kind === "identifier" ? "Full identifier" : "Full text") : undefined}
      data-side={position?.side}
      data-testid={testId ? `${testId}-popover` : undefined}
      style={{
        top: position?.top ?? 0,
        left: position?.left ?? 0,
        visibility: position ? "visible" : "hidden"
      } as CSSProperties}
      onPointerEnter={clearCloseTimer}
      onPointerLeave={scheduleClose}
      onPointerDown={onPanelPointerDown}
      onPointerUp={stopPanelEvent}
      onMouseDown={stopPanelEvent}
      onMouseUp={stopPanelEvent}
      onClick={stopPanelEvent}
      onDoubleClick={stopPanelEvent}
      onKeyDown={onPanelKeyDown}
      onFocusCapture={clearCloseTimer}
      onBlurCapture={scheduleClose}
    >
      <div ref={previewBodyRef} className="overflow-reveal-popover__body">
        {mode === "expanded" ? value : preview.text}
      </div>
      {copyable || expandable ? <footer>
        {copyable ? <button type="button" onClick={onCopy}>{copied ? "Copied" : copyFailed ? "Copy failed" : "Copy"}</button> : null}
        {expandable ? <button type="button" onClick={onToggleExpanded}>{mode === "expanded" ? "Show less" : "Show full"}</button> : null}
      </footer> : null}
    </aside>,
    document.body
  );

  return <>
    <Element
      ref={triggerRef as never}
      className={className}
      tabIndex={focusable ? 0 : undefined}
      aria-describedby={mode !== "closed" && !interactive ? id : undefined}
      aria-controls={mode !== "closed" && interactive && focusable ? id : undefined}
      aria-expanded={interactive && focusable ? mode !== "closed" : undefined}
      data-overflow-reveal-trigger=""
      data-testid={testId}
      onPointerEnter={openPreview}
      onPointerLeave={scheduleClose}
      onFocus={openPreview}
      onBlur={scheduleClose}
    >{children ?? shownValue}</Element>
    {panel}
  </>;
}
